/*
 * Copyright (c) 2017 Datarista, Inc.
 */

/* eslint-disable consistent-return */

import { polyfill } from 'es6-promise';
import { push } from 'react-router-redux';
import { destroy, reset } from 'redux-form';
import { Map } from 'immutable';
import { accountServiceClient } from '../utils/api';
import { startAction, completeAction, handleError, reportError, reportSuccess } from './common';

polyfill();

export const actionTypes = {
  ORDER_FETCH_ALL_SUCCESS: 'ORDER_FETCH_ALL_SUCCESS',
  ORDER_FETCH_ALL_ERROR: 'ORDER_FETCH_ALL_ERROR',
  PLATFORM_CASE_SUCCESS: 'PLATFORM_CASE_SUCCESS',
  ORDER_FETCH_BY_ID_SUCCESS: 'ORDER_FETCH_BY_ID_SUCCESS',
  RECEIVE_INSTALL_STATUS_SUCCESS: 'RECEIVE_INSTALL_STATUS_SUCCESS',
};

// Connect to the API
const swaggerApi = accountServiceClient();

// Create order list action
const receiveOrders = orders => ({
  type: actionTypes.ORDER_FETCH_ALL_SUCCESS,
  orders,
});

const receiveOrder = order => ({
  type: actionTypes.ORDER_FETCH_BY_ID_SUCCESS,
  order,
});

// routes user to next requested page in order form
export function goToPage(page, accountId) {
  const pageUrl = typeof accountId === 'undefined'
                  ? `/orders/create/${page}`
                  : `/orders/create/${page}/${accountId}`;
  return dispatch => {
    dispatch(push(pageUrl));
  };
}

export function goToPrematchedUploadPage(orderId) {
  return dispatch => {
    dispatch(push(`/orders/order/upload-prematched/${orderId}`));
  };
}

// re-reroutes the page if the user exits the form
export function cancelOrder() {
  return dispatch => {
    dispatch(destroy('createOrder'));
    dispatch(push('/orders'));
  };
}

// Resets the Update Credits form
export function cancelUpdateOrder() {
  return dispatch => {
    dispatch(reset('editOrderForm'));
    dispatch(reset('resendInstallEmailForm'));
  };
}

export function validationError(message) {
  return dispatch => {
    const error = new Error(message);
    reportError(dispatch, error);
  };
}

export function getOrderById(orderId, token) {
  const actionText = 'Getting order';
  return dispatch => {
    startAction(dispatch, actionText);
    return swaggerApi.then(client => {
      client.order
        .getOrder({
          Authorization: `bearer ${token}`,
          order_id: orderId,
        })
        .then(response => {
          if (response.status >= 200 && response.status < 300) {
            const orderInfo = response.obj;
            dispatch(receiveOrder(orderInfo));
            completeAction(dispatch, actionText);
          } else {
            return Promise.reject(response);
          }
        })
        .catch(err => {
          handleError(err, dispatch, actionText);
        });
    });
  };
}

// List all orders for logged in provider account
export function listOrders(token) {
  const actionText = 'Listing all orders';
  return dispatch => {
    startAction(dispatch, actionText);
    return swaggerApi.then(client => {
      client.order
        .getAllOrder({
          Authorization: `bearer ${token}`,
        })
        .then(response => {
          // If everything's cool, dispatch actions
          if (response.status >= 200 && response.status < 300) {
            const ordersInfo = response.obj;
            dispatch(receiveOrders(ordersInfo));
            completeAction(dispatch, actionText);
          } else {
            return Promise.reject(response);
          }
        })
        .catch(err => {
          handleError(err, dispatch, actionText);
        });
    });
  };
}

const listUseCaseSuccess = (platformMap, useCaseMap) => ({
  type: actionTypes.PLATFORM_CASE_SUCCESS,
  platformMap,
  useCaseMap,
});

// retrieves the platform and use case information
// for a given account
export function listUseCases(accountId, token) {
  const actionText = 'Listing all use cases';
  return dispatch => {
    startAction(dispatch, actionText);
    return swaggerApi.then(client => {
      client.accounts
        .getAccountPlatformUseCases({
          Authorization: `bearer ${token}`,
          account_id: accountId,
        })
        .then(response => {
          // If everything's cool, dispatch actions
          if (response.status >= 200 && response.status < 300) {
            const useCaseInfo = response.obj;
            let platformMap = new Map();
            let useCaseMap = new Map();
            useCaseInfo.forEach(platformCase => {
              const { platform } = platformCase;
              const { useCase } = platformCase;
              platformMap = platformMap.set(platform.code, platform);
              const prevValue = useCaseMap.get(platform.code);

              if (prevValue === undefined) {
                const newArray = [];
                newArray.push(useCase);
                useCaseMap = useCaseMap.set(platform.code, newArray);
              } else {
                prevValue.push(useCase);
                useCaseMap = useCaseMap.set(platform.code, prevValue);
              }
            });
            dispatch(listUseCaseSuccess(platformMap, useCaseMap));
            completeAction(dispatch, actionText);
          } else {
            return Promise.reject(response);
          }
        })
        .catch(err => {
          handleError(err, dispatch, actionText);
        });
    });
  };
}

// Create a new order
export function createNewOrder(token, orderRequest, isSuperAdmin) {
  const actionText = 'Creating new order';
  const pageUrl = isSuperAdmin
                  ? `/superadmin/orders`
                  : `/orders`;
  return dispatch => {
    startAction(dispatch, actionText);
    return swaggerApi.then(client => {
      client.order
        .createOrder({
          Authorization: `bearer ${token}`,
          body: orderRequest,
        })
        .then(response => {
          // If everything's cool, dispatch actions
          if (response.status >= 200 && response.status < 300) {
            dispatch(push(pageUrl));
            dispatch(destroy('createOrder'));
            reportSuccess(dispatch, 'New order created', null);
            completeAction(dispatch, actionText);
          } else {
            return Promise.reject(response);
          }
        })
        .catch(err => {
          handleError(err, dispatch, actionText);
        });
    });
  };
}

export function updateOrder(order, msg, token) {
  const actionText = `Updating order ${msg} for order ${order.id}`;
  return dispatch => {
    startAction(dispatch, actionText);
    return swaggerApi.then(client => {
      client.order
        .updateOrder({
          order_id: order.id,
          body: order,
          Authorization: `bearer ${token}`,
        })
        .then(response => {
          if (response.status >= 200 && response.status < 300) {
            dispatch(getOrderById(order.id, token));
            reportSuccess(dispatch, `${msg} Updated`, null);
            completeAction(dispatch, actionText);
          } else {
            return Promise.reject(response);
          }
        })
        .catch(err => {
          handleError(err, dispatch, actionText);
        });
    });
  };
}

export function uploadPrematchedPairs(orderId, recordType, file, token) {
  const actionText = `Uploading ${recordType.toUpperCase()} Prematched Pairs`;
  return dispatch => {
    startAction(dispatch, actionText);
    return swaggerApi.then(client => {
      client.order
        .uploadPreMatchedPairs({
          Authorization: `bearer ${token}`,
          order_id: orderId,
          record_type: recordType,
          file,
        })
        .then(response => {
          if (response.status >= 200 && response.status < 300) {
            const type = recordType.substring(0, 1).toUpperCase() + recordType.substring(1);
            const successMsg = `${type} match pair file has been saved successfully.`;
            reportSuccess(dispatch, successMsg, null);
            completeAction(dispatch, actionText);
          } else {
            return Promise.reject(response);
          }
        })
        .catch(err => {
          handleError(err, dispatch, actionText);
        });
    });
  };
}

export function uploadComplete(orderId, token) {
  const actionText = 'Update Uploading Prematched Pairs As Complete';
  return dispatch => {
    startAction(dispatch, actionText);
    return swaggerApi.then(client => {
      client.order
        .updatePreMatchedPairsUploadState({
          Authorization: `bearer ${token}`,
          order_id: orderId,
          body: JSON.stringify('UPLOAD_COMPLETE'),
        })
        .then(response => {
          if (response.status >= 200 && response.status < 300) {
            dispatch(push('/orders'));
            dispatch(destroy('prematchedPairsForm'));
            reportSuccess(dispatch, 'Updated: Prematched Pairs Upload Complete', null);
            completeAction(dispatch, actionText);
          }
        })
        .catch(err => {
          handleError(err, dispatch, actionText);
        });
    });
  };
}

const receiveInstallationStatusSuccess = install => ({
  type: actionTypes.RECEIVE_INSTALL_STATUS_SUCCESS,
  install,
});

export function getInstallationByOrderId(orderId, token) {
  const actionText = 'Getting installation for order';
  return dispatch => {
    startAction(dispatch, actionText);
    return swaggerApi.then(client => {
      client.order
        .getInstallationByOrderId({
          order_id: orderId,
          Authorization: `bearer ${token}`,
        })
        .then(response => {
          if (response.status >= 200 && response.status < 300) {
            const install = response.obj;
            dispatch(receiveInstallationStatusSuccess(install));
            completeAction(dispatch, actionText);
          } else {
            return Promise.reject(response);
          }
        })
        .catch(err => {
          const statusText = JSON.parse(err.statusText);
          const error = new Error(statusText.message);
          reportError(dispatch, error);
        });
    });
  };
}

export function updateInstall (install, msg, token) {
  const actionText = `Updating install ${msg} for install ${install.id}`;
  return dispatch => {
    startAction(dispatch, actionText);
    return swaggerApi.then(client => {
      client.order
        .updateInstallByOrderId({
          order_id: install.orderId,
          body: install,
          Authorization: `bearer ${token}`,
        })
        .then(response => {
          if (response.status >= 200 && response.status < 300) {
            const installInfo = response.obj;
            dispatch(receiveInstallationStatusSuccess(installInfo));
            reportSuccess(dispatch, `${msg} Updated`, null);
            completeAction(dispatch, actionText);
          } else {
            return Promise.reject(response);
          }
        })
        .catch(err => {
          handleError(err, dispatch, actionText);
        });
    });
  };
}

export function resendInstallEmail(email, install, msg, token) {
  const actionText = `Sending installation email for install ${install.id}`;
  return dispatch => {
    startAction(dispatch, actionText);
    return swaggerApi.then(client => {
      client.order
        .resendInstallEmail({
          email,
          body: install,
          Authorization: `bearer ${token}`,
        })
        .then(response => {
          if (response.status >= 200 && response.status < 300) {
            reportSuccess(dispatch, `Email sent successfully.`, null);
            completeAction(dispatch, actionText);
          } else {
            return Promise.reject(response);
          }
        })
        .catch(err => {
          handleError(err, dispatch, actionText);
        });
    });
  };
}